/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { PlanId } from "components/common/boundary/PlanId";
import { BasketRow, ProfileRow, Row } from "components/common/plan/types";
import { getMaterialMass } from "components/common/plan/utils";
import { Period } from "hooks/periodIndex";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";

import { RootState } from "store/store";
import { generatedApi } from "../api/generatedApi";

export type PlanPeriodId = string & { __planPeriodId__: true };

export interface PlanMixTableEdits {
  planId: PlanId;
  period: Period;
  editedRows: Row[];
  originalRows: Row[];
  isSubmitting: boolean;
  comment: string;
  mixIdsWithLayerEdits: number[];
  collapsedMixIds: number[];
}

export const makePlanPeriodId: (
  planId: PlanId,
  period: Period
) => PlanPeriodId = (planId, period) =>
  `${planId.toString()}-${period.toString()}` as PlanPeriodId;

export const parsePlanPeriodId: (
  planPeriodId: PlanPeriodId
) => [PlanId, Period] = (planPeriodId) => {
  const [planId, period] = planPeriodId.split("-");
  if (planId === undefined || period === undefined) {
    throw new Error("planPeriodId must have the form '<plan ID>-<period>'");
  }
  return [Number(planId), Number(period)] as [PlanId, Period];
};

const planMixTableEditsAdapter = createEntityAdapter({
  selectId: (planMixTableEdits: PlanMixTableEdits) =>
    makePlanPeriodId(planMixTableEdits.planId, planMixTableEdits.period),
});

export const planMixTableEditsSlice = createSlice({
  name: "planMixTableEdits",
  initialState: planMixTableEditsAdapter.getInitialState(undefined, []),
  reducers: {
    set(
      state,
      {
        payload: [planId, period, rows, comment],
      }: PayloadAction<[PlanId, Period, Row[], string?]>
    ) {
      planMixTableEditsAdapter.setOne(state, {
        planId,
        period,
        originalRows: rows,
        editedRows: rows,
        isSubmitting: false,
        comment: comment ?? "",
        mixIdsWithLayerEdits: [],
        collapsedMixIds: [],
      });
    },
    update(
      state,
      {
        payload: [planId, period, editedRows],
      }: PayloadAction<[PlanId, Period, Row[]]>
    ) {
      planMixTableEditsAdapter.updateOne(state, {
        id: makePlanPeriodId(planId, period),
        changes: { editedRows },
      });
    },
    clear(
      state,
      { payload: [planId, period] }: PayloadAction<[PlanId, Period]>
    ) {
      const planPeriodId = makePlanPeriodId(planId, period);
      const currentEntity = planMixTableEditsAdapter
        .getSelectors()
        .selectById(state, planPeriodId);

      if (currentEntity) {
        planMixTableEditsAdapter.updateOne(state, {
          id: planPeriodId,
          changes: {
            editedRows: currentEntity.originalRows,
            mixIdsWithLayerEdits: [],
          },
        });
      }
    },

    markMixAsLayerEdited(
      state,
      {
        payload: [planId, period, mixId],
      }: PayloadAction<[PlanId, Period, number]>
    ) {
      const planPeriodId = makePlanPeriodId(planId, period);
      const currentEntity = planMixTableEditsAdapter
        .getSelectors()
        .selectById(state, planPeriodId);

      if (
        currentEntity &&
        !currentEntity.mixIdsWithLayerEdits.includes(mixId)
      ) {
        planMixTableEditsAdapter.updateOne(state, {
          id: planPeriodId,
          changes: {
            mixIdsWithLayerEdits: [
              ...currentEntity.mixIdsWithLayerEdits,
              mixId,
            ],
          },
        });
      }
    },
    clearAllLayerEditMarks(
      state,
      { payload: [planId, period] }: PayloadAction<[PlanId, Period]>
    ) {
      planMixTableEditsAdapter.updateOne(state, {
        id: makePlanPeriodId(planId, period),
        changes: { mixIdsWithLayerEdits: [] },
      });
    },
    toggleCollapsedMix(
      state,
      {
        payload: [planId, period, mixId],
      }: PayloadAction<[PlanId, Period, number]>
    ) {
      const planPeriodId = makePlanPeriodId(planId, period);
      const currentEntity = planMixTableEditsAdapter
        .getSelectors()
        .selectById(state, planPeriodId);

      if (currentEntity) {
        const isCollapsed = currentEntity.collapsedMixIds.includes(mixId);
        planMixTableEditsAdapter.updateOne(state, {
          id: planPeriodId,
          changes: {
            collapsedMixIds: isCollapsed
              ? currentEntity.collapsedMixIds.filter(
                  (collapsedMixId) => collapsedMixId !== mixId
                )
              : [...currentEntity.collapsedMixIds, mixId],
          },
        });
      }
    },
    setIsSubmitting(
      state,
      { payload: [planId, period] }: PayloadAction<[PlanId, Period]>
    ) {
      planMixTableEditsAdapter.updateOne(state, {
        id: makePlanPeriodId(planId, period),
        changes: { isSubmitting: true },
      });
    },
    setClearSubmitting(
      state,
      { payload: [planId, period] }: PayloadAction<[PlanId, Period]>
    ) {
      planMixTableEditsAdapter.updateOne(state, {
        id: makePlanPeriodId(planId, period),
        changes: { isSubmitting: false },
      });
    },
    setComment(
      state,
      {
        payload: [planId, period, comment],
      }: PayloadAction<[PlanId, Period, string]>
    ) {
      planMixTableEditsAdapter.updateOne(state, {
        id: makePlanPeriodId(planId, period),
        changes: { comment },
      });
    },
    setMixDescription(
      state,
      {
        payload: [planId, period, mixId, description],
      }: PayloadAction<[PlanId, Period, number, string]>
    ) {
      const planPeriodId = makePlanPeriodId(planId, period);
      const currentEntity = planMixTableEditsAdapter
        .getSelectors()
        .selectById(state, planPeriodId);

      if (currentEntity) {
        const mixIndex = currentEntity.editedRows.findIndex(
          (row) => row.mixId === mixId
        );
        const existingRow = currentEntity.editedRows[mixIndex];
        if (mixIndex !== undefined && existingRow) {
          const updatedRow = {
            ...existingRow,
            description,
          };
          planMixTableEditsAdapter.updateOne(state, {
            id: planPeriodId,
            changes: {
              editedRows: [
                ...currentEntity.editedRows.slice(0, mixIndex),
                updatedRow,
                ...currentEntity.editedRows.slice(mixIndex + 1),
              ],
            },
          });
        }
      }
    },
  },
});

const createAppSelector = createSelector.withTypes<RootState>();

export const selectPlanMixTableEdits = createAppSelector(
  [
    (state) => state.planMixTableEdits,
    (_, planId: PlanId) => planId,
    (_, __, period: Period) => period,
  ],
  (planMixTableEdits, planId, period) => {
    return planMixTableEditsAdapter
      .getSelectors()
      .selectById(planMixTableEdits, makePlanPeriodId(planId, period));
  }
);

export const selectPlanMixTableEditIsSubmitting = createAppSelector(
  [selectPlanMixTableEdits],
  (tableEdits) => {
    return Boolean(tableEdits?.isSubmitting);
  }
);

export const selectPlanMixTableEditEditedRows = createAppSelector(
  [selectPlanMixTableEdits],
  (tableEdits) => {
    return tableEdits?.editedRows ?? [];
  }
);

export const selectPlanMixTableEditOriginalRows = createAppSelector(
  [selectPlanMixTableEdits],
  (tableEdits) => {
    return tableEdits?.originalRows ?? [];
  }
);

const emptyMixIdsWithLayerEdits: number[] = [];

export const selectMixIdsWithLayerEdits = createAppSelector(
  [selectPlanMixTableEdits],
  (tableEdits) => {
    return tableEdits?.mixIdsWithLayerEdits ?? emptyMixIdsWithLayerEdits;
  }
);

const emptyCollapsedMixIds: number[] = [];

export const selectCollapsedMixIds = createAppSelector(
  [selectPlanMixTableEdits],
  (tableEdits) => {
    return tableEdits?.collapsedMixIds ?? emptyCollapsedMixIds;
  }
);

export const selectMaterialHeatsMap = createAppSelector(
  [selectPlanMixTableEditEditedRows],
  (editedRows) => {
    return typeSafeObjectFromEntries(
      editedRows
        .filter((row: Row): row is ProfileRow => row.type === "profile")
        .map((row) => [row.mixId, row.numberOfHeats])
    );
  }
);

export const selectTotalConsumption = createAppSelector(
  [
    selectPlanMixTableEditEditedRows,
    selectMaterialHeatsMap,
    (_, __, ___: Period, materialName: string) => materialName,
  ],
  (editedRows, materialHeatsMap, materialName) => {
    return editedRows
      .filter((row: Row): row is ProfileRow | BasketRow =>
        ["basket", "profile"].includes(row.type)
      )
      .reduce((total, row) => {
        return (
          total +
          getMaterialMass(row.layers, materialName) *
            (materialHeatsMap[row.mixId] ?? 0)
        );
      }, 0);
  }
);

export const selectPeriodPlanEdits = createAppSelector(
  [
    (state) => state.planMixTableEdits,
    (_, planPeriodIds: PlanPeriodId[]) => planPeriodIds,
  ],
  (planMixTableEdits, planPeriodIds) => {
    return typeSafeObjectFromEntries(
      planPeriodIds.map((planPeriodId) => [
        planPeriodId,
        planMixTableEditsAdapter
          .getSelectors()
          .selectById(planMixTableEdits, planPeriodId),
      ])
    );
  }
);

export const selectLoadedPlanResults = createAppSelector(
  [
    (state) => state.api,
    (_, tenantName: string) => tenantName,
    (_, __, planPeriodIds: PlanPeriodId[]) => planPeriodIds,
  ],
  (api, tenantName, planPeriodIds) => {
    return typeSafeObjectFromEntries(
      planPeriodIds.map((planPeriodId) => {
        const [planId, period] = parsePlanPeriodId(planPeriodId);
        const queryResult = generatedApi.endpoints.getPlanResults.select({
          planId,
          tenantName,
          period,
        })({ api });
        return [planPeriodId, queryResult.data];
      })
    );
  }
);
